import { useEffect, useState } from 'react';
import Image from 'next/legacy/image';
import styled, { DefaultTheme } from 'styled-components';
import { Col, Row } from 'antd';
import { Gutter } from 'antd/lib/grid/row';
import { useMediaQuery } from 'react-responsive';
import TextClamp from 'react-string-clamp';

import useI18n from '../../common/hooks/useI18n';

type Props = {
  query: string;
  section: string;
  index: number;
  verticalMode: boolean;
  theme: DefaultTheme;
};

const Card = styled.div`
  position: relative;
  background-color: ${(props) => props.theme.colors.grey};
  padding: 28px;
  border-radius: ${(props) => props.theme.borderRadius};
  overflow: hidden;

  @media ${(props) => props.theme.breakpoints.smd} {
    padding: 24px;
  }
`;

const ImageAbsoluteContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;

const Number = styled.div`
  color: ${(props) => props.theme.colors.primary};
  font-size: 60px;
  font-weight: 500;

  @media ${(props) => props.theme.breakpoints.smd} {
    font-size: 32px;
  }
`;

const Title = styled.div<{ $verticalMode: boolean }>`
  font-size: ${(props) => (props.$verticalMode ? '16px' : '18px')};
  line-height: 140%;
  font-weight: 600;
  height: ${(props) => (props.$verticalMode ? '54px' : 'unset')};

  @media ${(props) => props.theme.breakpoints.smd} {
    font-size: 16px;
    height: unset;
  }
`;

const Description = styled.div<{ $verticalMode: boolean }>`
  font-size: 16px;
  line-height: 150%;
  margin-top: 16px;
  height: ${(props) => (props.$verticalMode ? '175px' : '75px')};

  @media ${(props) => props.theme.breakpoints.lgd} {
    font-size: 14px;
  }
  @media ${(props) => props.theme.breakpoints.smd} {
    height: ${(props) => (props.$verticalMode ? '147px' : '114px')};
  }
`;

const getObjectPosition = (
  verticalMode: boolean,
  isSmDown: boolean,
  i: number
): string => {
  if (verticalMode || !isSmDown) {
    return undefined;
  }
  return i % 2 === 0 ? `${100 - 5 * i}%` : `${5 * i}%`;
};

const PropertyCard: React.FC<Props> = ({
  query,
  section,
  index,
  verticalMode,
  theme,
}) => {
  const i18n = useI18n();
  const isSmDown = useMediaQuery({ query: theme.breakpoints.smd });

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const auxI = index + 1;

  let gutter: [Gutter, Gutter] = [0, 0];
  if (verticalMode) {
    gutter[1] = isSmDown ? 16 : 24;
  } else {
    gutter[0] = isSmDown ? 16 : 24;
  }

  let lines = 1;
  if (verticalMode) {
    lines = 7;
  } else {
    lines = isSmDown ? 6 : 3;
  }

  return (
    <Col xs={24} md={12} lg={verticalMode ? 6 : 12}>
      <Card>
        {isMounted && (
          <ImageAbsoluteContainer>
            <Image
              alt={i18n.t(
                `dynamic.sellHousePage.${query}.${section}PropertyTitle_${auxI}`
              )}
              layout="fill"
              objectFit={verticalMode ? 'contain' : 'cover'}
              objectPosition={getObjectPosition(verticalMode, isSmDown, index)}
              src={`/images/sell_house/${section}/banner_${
                !verticalMode && isSmDown ? (index % 2) + 1 : auxI
              }.svg`}
              sizes={theme.getImageSizes(
                '100vw',
                '100vw',
                '50vw',
                '50vw',
                '50vh'
              )}
            />
          </ImageAbsoluteContainer>
        )}
        <Row gutter={gutter} align="top">
          <Col
            flex={verticalMode ? undefined : 'none'}
            span={verticalMode ? 24 : undefined}
            style={{ alignSelf: 'center' }}
          >
            <Number>0{auxI}.</Number>
          </Col>
          <Col style={{ flex: 1 }}>
            <Title as="div" $verticalMode={verticalMode}>
              {i18n.t(
                `dynamic.sellHousePage.${query}.${section}PropertyTitle_${auxI}`
              )}
            </Title>

            <Description as="div" $verticalMode={verticalMode}>
              <TextClamp
                text={i18n.t(
                  `dynamic.sellHousePage.${query}.${section}PropertySubtitle_${auxI}`
                )}
                lines={lines}
                element="p"
              />
            </Description>
          </Col>
        </Row>
      </Card>
    </Col>
  );
};

export default PropertyCard;
